import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FirstAidKit, Sparkle, Trash, CheckCircle, CalendarCheck } from "@phosphor-icons/react";

const fmtD = (ds) => new Date(ds + "T12:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "long" });

export const ProtocolCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState([]);

  const load = () => api.get("/labs/protocol").then((r) => {
    setData(r.data);
    if (r.data.protocol && !r.data.protocol.adopted) setChecked(r.data.protocol.habits.map((_, i) => i));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/labs/protocol/generate", {}, { timeout: 120000 });
      toast.success("Протокол готов — посмотрите план");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const adopt = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/labs/protocol/${data.protocol.id}/adopt`, { habit_indices: checked });
      toast.success(`Привычек создано: ${r.habits_created} · пересдача ${fmtD(r.retest_date)} в календаре`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    await api.delete(`/labs/protocol/${data.protocol.id}`).catch(() => {});
    load();
  };

  if (!data || (!data.protocol && !data.has_deficits)) return null;
  const p = data.protocol;

  return (
    <div className="glass-card p-5 mb-4 border-t-2 border-t-[#e879f9]/50" data-testid="protocol-card">
      <div className="flex items-center gap-2 mb-1">
        <FirstAidKit size={18} weight="fill" className="text-[#e879f9]" />
        <h3 className="text-sm font-semibold flex-1">Протокол устранения дефицитов</h3>
        {p && <button onClick={remove} data-testid="protocol-delete" className="text-muted-foreground hover:text-red-400"><Trash size={15} /></button>}
      </div>

      {!p ? (
        <>
          <p className="text-xs text-muted-foreground mb-3">В ваших анализах найдены дефициты — AI-коуч составит план: питание, привычки для трекера и дата контрольной пересдачи.</p>
          <button onClick={generate} disabled={busy} data-testid="protocol-generate-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#e879f9] text-black text-xs font-bold disabled:opacity-50 hover:brightness-110 transition-all">
            <Sparkle size={15} weight="fill" /> {busy ? "AI составляет протокол..." : "Составить протокол"}
          </button>
        </>
      ) : (
        <>
          {p.summary && <p className="text-xs text-muted-foreground mb-3">{p.summary}</p>}
          <div className="space-y-2.5 mb-4">
            {p.items.map((it, i) => (
              <div key={i} className="rounded-lg bg-white/[0.04] border border-white/[0.07] px-3.5 py-3" data-testid={`protocol-item-${i}`}>
                <div className="flex items-baseline gap-2 mb-1.5">
                  <span className="text-xs font-bold text-[#e879f9]">{it.marker}</span>
                  <span className="text-[10px] text-muted-foreground">{it.goal}</span>
                </div>
                <ul className="space-y-1">
                  {it.actions.map((a, ai) => (
                    <li key={ai} className="text-[11px] flex gap-1.5"><span className="text-[#e879f9]">•</span>{a}</li>
                  ))}
                </ul>
                {it.foods.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {it.foods.map((f, fi) => <span key={fi} className="text-[10px] px-2 py-0.5 rounded-full bg-[#00e676]/10 text-[#00e676]">{f}</span>)}
                  </div>
                )}
                {it.caution && <p className="text-[10px] text-[#ffb340] mt-1.5">⚕️ {it.caution}</p>}
              </div>
            ))}
          </div>

          {!p.adopted ? (
            <>
              <div className="text-xs font-semibold mb-2">Привычки для трекера:</div>
              <div className="space-y-1.5 mb-3">
                {p.habits.map((h, i) => (
                  <label key={i} className="flex items-start gap-2 cursor-pointer" data-testid={`protocol-habit-${i}`}>
                    <input type="checkbox" checked={checked.includes(i)}
                      onChange={(e) => setChecked(e.target.checked ? [...checked, i] : checked.filter((x) => x !== i))} />
                    <span className="text-xs"><b>{h.name}</b> <span className="text-muted-foreground">— {h.why}</span></span>
                  </label>
                ))}
              </div>
              <button onClick={adopt} disabled={busy} data-testid="protocol-adopt-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#00e676] text-black text-xs font-bold disabled:opacity-50 hover:brightness-110 transition-all">
                <CheckCircle size={15} weight="fill" /> Принять: создать привычки + пересдача через {p.retest_days} дн.
              </button>
            </>
          ) : (
            <div data-testid="protocol-progress">
              <div className="text-xs font-semibold mb-2">Контроль прогресса (14 дней):</div>
              <div className="space-y-2 mb-3">
                {data.progress.map((pr, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span>{pr.name}</span><b className="num">{pr.done_14d}/14</b>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(pr.done_14d / 14) * 100}%`, background: pr.color || "#e879f9" }} />
                    </div>
                  </div>
                ))}
                {data.progress.length === 0 && <p className="text-[11px] text-muted-foreground">Привычки не создавались — прогресс по пересдаче.</p>}
              </div>
              {p.retest_event && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <CalendarCheck size={14} className="text-[#4dabf5]" /> Контрольная пересдача: <b>{fmtD(p.retest_event.date)}</b> (в календаре)
                </p>
              )}
              <button onClick={generate} disabled={busy} data-testid="protocol-regenerate-btn"
                className="mt-3 text-[11px] font-semibold text-[#e879f9] hover:brightness-125">
                {busy ? "AI составляет..." : "↻ Составить новый протокол"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
